// CompaniesTab.jsx — 테넌트 관리 "회사" 탭 (회사 추가 + 목록 + 회사 허브 상세 아코디언).
//   SaasTenantManagement.jsx 에서 분리(2026-07-14). 회사 클릭→기본정보(운영자 편집)·메뉴 안내.
import React, { useState, useEffect, useCallback } from 'react';
import { apiClient } from '../../../api/apiClient';
import { input, btn, card, th, td, statusBadge, TenantPicker } from './saasShared';
import CompanyInitialSetupSection from './CompanyInitialSetupSection';
import ConfidentialLockPanel from '../confidential/ConfidentialLockPanel';
import CompanyMenuPanel from './CompanyMenuPanel';

export default function CompaniesTab({ tenants, selected, setSelected, showToast, onChanged, isOperator }) {
  const [companies, setCompanies] = useState([]);
  const [form, setForm] = useState({ code: '', name: '' });
  const [expanded, setExpanded] = useState(null); // 펼친 회사 code
  const load = useCallback(async () => {
    if (!selected) return;
    const res = await apiClient.get(`/api/admin/saas/tenants/${selected}/companies`);
    if (res.ok) setCompanies(res.data?.data || []);
  }, [selected]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { setExpanded(null); }, [selected]); // 테넌트 바꾸면 아코디언 닫기

  async function addCompany() {
    const code = form.code.trim().toUpperCase();
    if (!code || !form.name.trim()) { showToast('회사 코드와 이름을 입력하세요.', 'error'); return; }
    // 생성+연결을 서버 한 트랜잭션으로(createIfMissing) — 예전 2회 호출은 연결 실패 시
    //   고아 외부회사가 남았고, 테넌트 셀프 경로와 시드가 달랐다. 첫 활성 회사는 자동 대표.
    const link = await apiClient.post(`/api/admin/saas/tenants/${selected}/companies`, { code, name: form.name, createIfMissing: true });
    if (!link.ok) { showToast(link.data?.message || '회사 추가 실패', 'error'); return; }
    showToast('회사를 테넌트에 추가했습니다.', 'success');
    setForm({ code: '', name: '' });
    await load(); await onChanged();
  }

  return (
    <>
      <div style={{ ...card, display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
        <strong style={{ fontSize: 13 }}>테넌트</strong>
        <TenantPicker tenants={tenants} selected={selected} setSelected={setSelected} />
      </div>
      <div style={{ ...card, display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
        <strong style={{ fontSize: 13 }}>회사 추가</strong>
        <input style={{ ...input, width: 140 }} placeholder="회사코드" value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase().replace(/[^A-Z0-9_]/g, '') })} />
        <input style={{ ...input, flex: 1, minWidth: 140 }} placeholder="회사 이름" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
        <button style={btn} onClick={addCompany}>추가</button>
        <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>외부회사 생성(권한·급여항목·부서 자동) + 테넌트 연결</span>
      </div>
      <div style={card}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead><tr><th style={{ ...th, width: 22 }}></th><th style={th}>코드</th><th style={th}>이름</th><th style={th}>유형</th><th style={th}>대표</th><th style={th}>상태</th></tr></thead>
          <tbody>
            {companies.length === 0 && <tr><td style={td} colSpan={6}>연결된 회사가 없습니다.</td></tr>}
            {companies.map((c) => (
              <React.Fragment key={c.code}>
                <tr onClick={() => setExpanded((p) => (p === c.code ? null : c.code))} style={{ cursor: 'pointer', background: expanded === c.code ? 'var(--bg-subtle)' : 'transparent' }}>
                  <td style={{ ...td, color: 'var(--text-muted)', textAlign: 'center' }}>{expanded === c.code ? '▾' : '▸'}</td>
                  <td style={td}>{c.code}</td>
                  <td style={td}>{c.external_name || c.name}</td>
                  <td style={td}>{c.is_primary ? '대표' : '운영'}</td>
                  <td style={td}>{c.is_primary ? '✓' : ''}</td>
                  <td style={td}>{c.is_active ? statusBadge('active') : statusBadge('inactive')}</td>
                </tr>
                {expanded === c.code && (
                  <tr>
                    <td style={{ ...td, background: 'var(--bg-subtle)', padding: 12 }} colSpan={6}>
                      <CompanyInlinePanel company={c} tenantCode={selected} isOperator={isOperator} showToast={showToast} onSaved={async () => { await load(); await onChanged(); }} />
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>
      <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>테넌트 관리자 계정(아이디 확인·비번 초기화·생성)은 <strong>테넌트 탭</strong>에서 각 테넌트 행의 '관리자' 버튼으로 관리합니다.</div>
    </>
  );
}

// 회사 상세 인라인 패널 — 기본정보(운영자만 회사명·법인명·코드 편집) + 활성 토글 + 메뉴 안내.
//   2026-09-20: 동적 관리 > 회사 설정에 있던 **활성 토글·코드 변경**을 여기로 옮겼다(사용자 확정).
//   회사 관련 일이 두 군데로 갈려 있어 한쪽만 고치기 쉬웠다. 위험한 두 동작에는 확인창을 붙인다.
//   편집에 필요한 external_companies.id·name·company_name 은 회사 목록(listTenantCompanies)이 제공 → 별도 조회 없음.
function CompanyInlinePanel({ company, tenantCode, isOperator, showToast, onSaved }) {
  const [name, setName] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [code, setCode] = useState('');
  const [saving, setSaving] = useState(false);
  const [promoting, setPromoting] = useState(false);
  const [togglingActive, setTogglingActive] = useState(false);

  // 대표 지정 — 대표 회사는 "예산 즉시 적용" 파생의 기준이라 테넌트당 1개여야 한다(운영자 전용).
  async function promoteToPrimary() {
    if (!window.confirm(`'${company.code}' 를 이 테넌트의 대표 회사로 지정할까요?\n기존 대표는 운영 회사로 바뀌고, 관리자 메뉴 구성이 즉시 달라질 수 있습니다.`)) return;
    setPromoting(true);
    const res = await apiClient.put(`/api/admin/saas/tenants/${tenantCode}/companies/${company.code}/primary`);
    setPromoting(false);
    if (!res.ok) { showToast(res.data?.message || '대표 지정 실패', 'error'); return; }
    showToast('대표 회사를 변경했습니다.', 'success');
    if (onSaved) await onSaved();
  }
  useEffect(() => {
    setName(company.external_name || company.name || '');
    setCompanyName(company.company_name || '');
    setCode(company.code || '');
  }, [company.code, company.external_name, company.company_name, company.name]);

  const extId = company.external_company_id;
  const canEdit = Boolean(isOperator && extId); // 운영자 + 외부회사 레코드 존재(PINE 등 고정 회사 제외)

  async function save() {
    if (!extId) { showToast('편집할 회사 정보를 찾지 못했습니다.', 'error'); return; }
    if (!name.trim()) { showToast('회사명은 필수입니다.', 'error'); return; }
    const nextCode = String(code || '').toUpperCase().replace(/[^A-Z0-9]/g, '');
    if (!nextCode) { showToast('코드는 필수입니다.', 'error'); return; }
    const body = { name: name.trim(), company_name: companyName.trim() || null };
    // 코드 변경은 company_code 를 가진 표 전부와 권한 코드·설정 키까지 한꺼번에 바꾼다.
    //   되돌리려면 같은 작업을 반대로 해야 하므로 반드시 확인을 받는다(전에는 확인 없이 저장됐다).
    if (nextCode !== String(company.code || '').toUpperCase()) {
      if (!window.confirm(`회사 코드를 '${company.code}' → '${nextCode}' 로 바꿀까요?\n\n직원·출퇴근·콜·정산 등 이 회사 코드를 쓰는 모든 기록과 권한 이름이 함께 바뀝니다.\n되돌리려면 같은 작업을 반대로 해야 합니다.`)) return;
      body.new_code = nextCode;
    }
    setSaving(true);
    const res = await apiClient.put(`/api/admin/external-companies/${extId}`, body);
    setSaving(false);
    if (!res.ok) { showToast(res.data?.message || '저장 실패', 'error'); return; }
    showToast('회사 정보를 저장했습니다.', 'success');
    if (onSaved) await onSaved();
  }

  // 활성/비활성 — 끄면 그 회사 메뉴·기능이 사라지므로 확인을 받는다.
  async function toggleActive() {
    if (!extId) { showToast('편집할 회사 정보를 찾지 못했습니다.', 'error'); return; }
    const turningOff = company.is_active !== false;
    const msg = turningOff
      ? `'${company.code}' 회사를 끌까요?\n\n이 회사 메뉴와 기능이 보이지 않게 됩니다. 기록은 지워지지 않습니다.`
      : `'${company.code}' 회사를 다시 켤까요?`;
    if (!window.confirm(msg)) return;
    setTogglingActive(true);
    const res = await apiClient.put(`/api/admin/external-companies/${extId}`, { is_active: !company.is_active });
    setTogglingActive(false);
    if (!res.ok) { showToast(res.data?.message || '변경 실패', 'error'); return; }
    showToast(turningOff ? '회사를 껐습니다.' : '회사를 켰습니다.', 'success');
    if (onSaved) await onSaved();
  }

  const labelStyle = { fontSize: 12, color: 'var(--text-muted)' };
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <div>
        <div style={{ fontSize: 12, fontWeight: 800, marginBottom: 6 }}>기본정보</div>
        <div style={{ display: 'grid', gridTemplateColumns: '72px minmax(0, 1fr)', gap: 8, alignItems: 'center', maxWidth: 460 }}>
          <span style={labelStyle}>코드</span>
          <span style={{ fontSize: 13, fontWeight: 700, display: 'inline-flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
            {canEdit
              ? <input style={{ ...input, width: 120, fontWeight: 700 }} value={code}
                  onChange={(e) => setCode(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, ''))} />
              : company.code}
            <span style={{ fontSize: 11, color: 'var(--text-muted)', fontWeight: 400 }}>({company.is_primary ? '대표' : '운영'} · {company.is_active ? '활성' : '비활성'})</span>
            {isOperator && !company.is_primary && company.is_active && (
              <button style={{ ...btn, fontSize: 11, padding: '2px 8px' }} onClick={promoteToPrimary} disabled={promoting}>
                {promoting ? '지정 중…' : '대표 지정'}
              </button>
            )}
            {canEdit && (
              <button style={{ ...btn, fontSize: 11, padding: '2px 8px', background: company.is_active ? '#dc2626' : undefined }}
                onClick={toggleActive} disabled={togglingActive}>
                {togglingActive ? '변경 중…' : (company.is_active ? '끄기' : '켜기')}
              </button>
            )}
          </span>
          <span style={labelStyle}>회사명</span>
          {canEdit ? <input style={input} value={name} onChange={(e) => setName(e.target.value)} /> : <span style={{ fontSize: 13 }}>{name || '-'}</span>}
          <span style={labelStyle}>법인명</span>
          {canEdit ? <input style={input} value={companyName} onChange={(e) => setCompanyName(e.target.value)} placeholder="(선택)" /> : <span style={{ fontSize: 13 }}>{companyName || '-'}</span>}
        </div>
        {canEdit && (
          <div style={{ marginTop: 8, display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            <button style={btn} onClick={save} disabled={saving}>{saving ? '저장 중…' : '저장'}</button>
            <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>코드를 바꾸면 이 회사 기록·권한 이름이 함께 바뀝니다. 메뉴 켜고 끄기는 아래 <strong>메뉴</strong>에서 합니다.</span>
          </div>
        )}
        {isOperator && !extId && <div style={{ marginTop: 6, fontSize: 11, color: 'var(--text-muted)' }}>고정 회사(외부회사 레코드 없음) — 편집 대상이 아닙니다.</div>}
        {!isOperator && <div style={{ marginTop: 6, fontSize: 11, color: 'var(--text-muted)' }}>회사 정보 편집은 운영자만 가능합니다.</div>}
      </div>
      {isOperator && (
        <div style={{ borderTop: '1px dashed var(--border)', paddingTop: 8 }}>
          {/* 회사별 대외비 비밀번호(2026-10-16, 급여·연차정산·흐름) — 운영자가 회사마다 정한다 */}
          <ConfidentialLockPanel tenantCode={tenantCode} onlyCode={company.code} />
        </div>
      )}
      <div style={{ borderTop: '1px dashed var(--border)', paddingTop: 8 }}>
        <CompanyInitialSetupSection company={company} />
      </div>
      <div style={{ borderTop: '1px dashed var(--border)', paddingTop: 8 }}>
        {/* 회사별 메뉴(2026-10-19 사용자 규칙 — 테넌트가 받은 메뉴 안에서, 운영사 회사도 같다) */}
        <CompanyMenuPanel tenantCode={tenantCode} company={company} showToast={showToast} />
      </div>
    </div>
  );
}
